import { CSSProperties, memo } from 'react';
import { Link } from '@client/router/compat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faToggleOn,
  faToggleOff,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { Badge } from '@design-system/components/badge/badge';
import { Chip } from '@design-system/components/chip/chip';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import auth from '@client/components/auth';
import { PATHS } from '@client/routes';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { PortalUserRowVM } from '../../view-model/PortalUserList.viewModel.mappers';
import {
  statusBadgeActive,
  statusBadgeDisabled,
  statusBadgeWrapper,
} from '../PortalUserList.view.css';
import * as styles from './PortalUserListItem.css';

type Props = {
  row: PortalUserRowVM;
  style?: CSSProperties;
  onToggle: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
};

const LinkedCreatorChip = ({ row }: { row: PortalUserRowVM }) => {
  if (row.influencerId === null) {
    return <span className={styles.linkedChipEmpty}>—</span>;
  }

  const path = PATHS.HOME_CREATOR(row.influencerId);

  return (
    <Link
      to={path}
      onClick={() => trackSessionEvent('Navigate', { path })}
      className={styles.actionLink}
    >
      <Chip label={`Creator #${row.influencerId}`} />
    </Link>
  );
};

const toggleCopy = (isActive: boolean) =>
  isActive
    ? {
        label: 'Disable user',
        tooltip:
          'Active. Disabling blocks their login and ends their open sessions.',
      }
    : {
        label: 'Enable user',
        tooltip: 'Disabled. Enabling lets them log in again.',
      };

const StatusBadge = ({ isActive }: { isActive: boolean }) => (
  <span
    className={`${statusBadgeWrapper} ${
      isActive ? statusBadgeActive : statusBadgeDisabled
    }`}
  >
    <Badge label={isActive ? 'Active' : 'Disabled'} />
  </span>
);

export const PortalUserListItem = memo(
  ({ row, style, onToggle, onEdit, onDelete }: Props) => {
    const isAdministrator = auth.isAdministrator();
    const toggle = toggleCopy(row.isActive);

    return (
      <div className={styles.row} style={style}>
        <div className={styles.nameCell}>
          <span className={styles.nameLine}>
            {row.fullName} <span className={styles.nameId}>({row.id})</span>
          </span>
          <span className={styles.emailLine}>{row.email}</span>
        </div>

        <span className={styles.cellText}>{row.roleLabel}</span>

        <StatusBadge isActive={row.isActive} />

        <div className={styles.linkedChipWrapper}>
          <LinkedCreatorChip row={row} />
        </div>

        <div className={styles.actionCell}>
          {isAdministrator ? (
            <Tooltip variant="dark" title={toggle.tooltip}>
              <span>
                <button
                  type="button"
                  className={
                    row.isActive
                      ? styles.toggleOnButton
                      : styles.toggleOffButton
                  }
                  disabled={row.isPending}
                  onClick={() => onToggle(row.id)}
                  aria-label={toggle.label}
                >
                  <FontAwesomeIcon
                    icon={row.isActive ? faToggleOn : faToggleOff}
                  />
                </button>
              </span>
            </Tooltip>
          ) : null}
          {isAdministrator ? (
            <Tooltip
              variant="dark"
              title="Edit name, e-mail, role and linked creator"
            >
              <span>
                <button
                  type="button"
                  className={styles.iconButton}
                  disabled={row.isPending}
                  onClick={() => onEdit(row.id)}
                  aria-label="Edit user"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              </span>
            </Tooltip>
          ) : null}
          {isAdministrator ? (
            <Tooltip
              variant="dark"
              title="Delete the user for good. You confirm before anything is removed."
            >
              <span>
                <button
                  type="button"
                  className={styles.deleteButton}
                  disabled={row.isDeleting}
                  onClick={() => onDelete(row.id)}
                  aria-label="Delete user"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </span>
            </Tooltip>
          ) : null}
        </div>
      </div>
    );
  },
);

PortalUserListItem.displayName = 'PortalUserListItem';
